import { useEffect, useState } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Box,
  Tooltip,
  Grid,
  Chip,
  Typography,
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
import "./Analytics.css";

export default function MultiSelectFilter({
  label,
  value,
  options,
  onChange,
  isMultiple,
  onMetaChange,
  loading = false,
  disabled = false,
}) {
  const getOptionValue = (item) => {
    if (typeof item === "string") return item;
    if (typeof item === "object" && item !== null)
      return item?.origin_port ?? "";
    return "";
  };

  const getOptionLabel = (item) => {
    if (typeof item === "string") return item;
    if (typeof item === "object" && item !== null) {
      const port = item.origin_port ?? "";
      const country = item.origin_country ?? "";
      if (port && country) return `${port} (${country})`;
      if (port) return port;
      if (country) return country;
      return "";
    }
    return "";
  };

  const getLabel = (item) => {
    if (typeof item === "string") return item;
    if (!item || typeof item !== "object") return "";
    if (item.origin_port && item.origin_country) {
      return `${item.origin_port} (${item.origin_country})`;
    } else if (item?.name && item?.id) {
      return `${item?.name}`;
    }
    const firstValue = Object.values(item).find(
      (v) => typeof v === "string" || typeof v === "number"
    );
    return firstValue ? String(firstValue) : "";
  };

  // Explicitly controlled input text for the single-select Autocomplete —
  // kept in lockstep with `value` so the visible text can never linger
  // out of sync with the actual selection (which is what let a previous
  // selection's text stay on screen after the field it depends on, and
  // this field's own value, had already changed).
  const singleSelectValue = Array.isArray(value) ? null : value || null;
  const [inputValue, setInputValue] = useState(getLabel(singleSelectValue));
  useEffect(() => {
    setInputValue(getLabel(singleSelectValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getLabel(singleSelectValue)]);

  return (
    <>
      <Grid item xs={12} lg={6} sx={{ minWidth: 0 }}>
        <Box className="label_name">
          {/* <img src={originCountryImg} alt="img" /> */}
          &nbsp;
          {/* <Tooltip
            title="Use this drop down to filter the required origin country in the table below."
            placement="right"
          >
          </Tooltip> */}
          <span className="label-style">{label}</span>
        </Box>
        <FormControl fullWidth size="small">
          {isMultiple ? (
            /* 🔹 MULTI SELECT (Checkbox) */
            <Select
              multiple
              fullWidth
              disabled={disabled}
              labelId="demo-multiple-checkbox-label"
              id="demo-multiple-checkbox"
              // value={value}
              value={isMultiple ? value : value || ""}
              onChange={(e) => {
                const selectedValues = e.target.value; // string[]
                onChange(selectedValues);
                const selectedObjects = options.filter((item) => {
                  return selectedValues.includes(getOptionValue(item));
                });
                const payloadData = selectedObjects.map((item) => ({
                  origin_country: item.origin_country,
                  origin_port: item.origin_port,
                }));
                if (typeof onMetaChange === "function") {
                  onMetaChange(payloadData);
                }
              }}
              endAdornment={
                loading ? (
                  <CircularProgress size={18} sx={{ mr: 2 }} />
                ) : null
              }
              sx={{
                minWidth: 0, // 🔑 flex/grid fix
                borderRadius: "10px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e2e5ea",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#c9cdd6",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#592DFF",
                  borderWidth: "1.5px",
                },
                "& .MuiSelect-select": {
                  display: "block", // 🔑 stop inline-flex growth
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: 13,
                  fontWeight: 600,
                  text: "bold",
                },
              }}
              displayEmpty
              renderValue={(selected) =>
                selected.length > 0 ? (
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      width: "100%",
                      display: "block",
                    }}
                  >
                    {selected.join(", ")}
                  </Typography>
                ) : (
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 400,
                      color: "#aaa",
                      opacity: 1,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      width: "100%",
                      display: "block",
                    }}
                  >
                    Select {label}
                  </Typography>
                )
              }
            >
              {Array.isArray(options) &&
                options.map((item, index) => {
                  const optionValue = getOptionValue(item);
                  const optionLabel = getOptionLabel(item);
                  return (
                    <MenuItem
                      key={`${optionValue}-${index}`}
                      value={optionValue}
                      sx={{
                        minHeight: 36, // ⬅️ same as Autocomplete
                        py: 0.0, // vertical padding
                        px: 1.5, // horizontal padding
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontSize: 13,
                        fontWeight: 400,
                      }}
                    >
                      <Checkbox checked={value?.includes(optionValue)} />
                      <ListItemText
                        primary={optionLabel}
                        // primaryTypographyProps={{ noWrap: true }}
                        primaryTypographyProps={{
                          noWrap: true,
                          fontSize: 13,
                          fontWeight: 400,
                          lineHeight: "20px",
                        }}
                        sx={{
                          m: 0,
                        }}
                      />
                    </MenuItem>
                  );
                })}
            </Select>
          ) : (
            <Autocomplete
              loading={loading}
              disabled={disabled}
              options={options || []}
              value={singleSelectValue}
              inputValue={inputValue}
              onInputChange={(_, newInputValue, reason) => {
                // Only track the user actually typing — a "reset" reason
                // fires on every value/options change and would otherwise
                // fight with the controlled useEffect above.
                if (reason === "input") {
                  setInputValue(newInputValue);
                }
              }}
              onChange={(_, newValue) => {
                if (label === "Tariff") {
                  onChange(newValue?.id);
                } else {
                  onChange(newValue);
                }
              }}
              // ✅ ALWAYS string for UI
              getOptionLabel={getLabel}
              // ✅ Prevents MUI mismatch warnings
              isOptionEqualToValue={(option, value) =>
                getLabel(option) === getLabel(value)
              }
              sx={{
                minWidth: 0, // 🔑 flex/grid fix
                "& .MuiInputBase-input": {
                  color: "#0f172a", // 👈 selected text color
                  fontSize: 13,
                  fontWeight: 600,
                },
              }}
              // ✅ Safe dropdown rendering
              renderOption={(props, option) => (
                <li {...props} key={getLabel(option)}>
                  {getLabel(option)}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={`Select ${label}`}
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: "13px !important",
                      fontWeight: 600,
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "#aaa !important",
                      opacity: 1,
                      fontSize: "13px !important",
                      fontWeight: 400,
                    },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      "& fieldset": { borderColor: "#e2e5ea" },
                      "&:hover fieldset": { borderColor: "#c9cdd6" },
                      "&.Mui-focused fieldset": {
                        borderColor: "#592DFF",
                        borderWidth: "1.5px",
                      },
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
        </FormControl>
      </Grid>
    </>
  );
}
